/**
 * Las reglas de adjuntos que aplica el navegador, en un solo sitio.
 *
 * POR QUÉ EXISTE ESTE FICHERO
 * Hay dos sitios que suben adjuntos —el panel de una tarea ya creada y el área de alta— y
 * dos copias de las mismas reglas acaban divergiendo: se añade un formato en uno y el otro
 * lo sigue rechazando durante meses sin que nadie entienda por qué. Una sola definición.
 *
 * ESTO NO SUSTITUYE LA VALIDACIÓN DEL SERVIDOR, y no puede.
 * El servidor mira los primeros bytes del fichero (ver `FIRMAS` en `attachments.controller`)
 * porque el `type` que reporta el navegador lo controla quien sube: un `.php` renombrado a
 * `.png` llega marcado como imagen. Aquí solo se comprueba lo que el navegador dice ser.
 *
 * Entonces, ¿para qué comprobarlo aquí? Para que los dos errores frecuentes —el formato que
 * no toca y el fichero demasiado pesado— salgan ANTES de crear la tarea. Al dar de alta, un
 * rechazo del servidor llega cuando la tarea ya existe, y entonces el aviso tiene que
 * explicar un estado a medias en vez de simplemente impedirlo.
 */

/** Los que el servidor sabe reconocer por su firma. Si cambian allí, cambian aquí. */
export const TIPOS_IMAGEN = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

/**
 * Vídeo. `video/quicktime` es lo que graba un iPhone.
 *
 * AVISO SOBRE EL .MOV: se acepta porque la mayoría son H.264 y se reproducen bien, pero un
 * iPhone reciente graba en HEVC y ESO no lo reproduce Chrome ni Firefox. No se puede saber
 * cuál es sin decodificar el fichero, así que no se rechaza a ciegas —dejaría fuera todos
 * los que sí funcionan—. Por eso la interfaz siempre ofrece descargar el adjunto: cuando el
 * navegador no puede con él, queda una salida en vez de un recuadro negro.
 */
export const TIPOS_VIDEO = ['video/mp4', 'video/quicktime', 'video/webm'] as const;

/**
 * Documentos, por EXTENSIÓN y no por `type`.
 *
 * Con el texto el `type` del navegador no sirve: un `.md` llega con `text/markdown`, con
 * `text/x-markdown` o vacío según el sistema, y un `.csv` en Windows llega como
 * `application/vnd.ms-excel`. La extensión es lo único estable. El servidor decide igual:
 * firma para PDF y Office, extensión + UTF-8 válido para el texto.
 */
export const EXTENSIONES_DOCUMENTO = [
  'pdf',
  'docx',
  'xlsx',
  'pptx',
  'md',
  'markdown',
  'txt',
  'log',
  'csv',
  'json',
  'yml',
  'yaml',
] as const;

/** Para el `accept` del `<input type="file">`. Es una ayuda del diálogo, no una barrera. */
export const ACEPTA = [
  ...TIPOS_IMAGEN,
  ...TIPOS_VIDEO,
  ...EXTENSIONES_DOCUMENTO.map((e) => `.${e}`),
].join(',');

export type ClaseAdjunto = 'imagen' | 'video' | 'documento';

/** Los techos del servidor, en MB, uno por clase. Llegan en la sesión (`/api/auth/me`). */
export interface LimitesMb {
  imagen: number;
  video: number;
  documento: number;
}

/** ¿Se pinta con `<video>` en vez de con `<img>`? */
export function esVideo(mime: string): boolean {
  return mime.startsWith('video/');
}

/** ¿Se pinta como miniatura (imagen o vídeo) o como ficha de documento? */
export function esMultimedia(mime: string): boolean {
  return mime.startsWith('image/') || mime.startsWith('video/');
}

/** La extensión en mayúsculas, para la ficha de un documento: «PDF», «MD», «XLSX». */
export function etiquetaExtension(nombre: string): string {
  return /\.([a-z0-9]+)$/i.exec(nombre)?.[1]?.toUpperCase() ?? 'FICHERO';
}

/** Qué clase de adjunto parece ser, o `null` si no es de ningún tipo admitido. */
export function claseDe(file: File): ClaseAdjunto | null {
  if ((TIPOS_IMAGEN as readonly string[]).includes(file.type)) return 'imagen';
  if ((TIPOS_VIDEO as readonly string[]).includes(file.type)) return 'video';
  const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase() ?? '';
  return (EXTENSIONES_DOCUMENTO as readonly string[]).includes(ext) ? 'documento' : null;
}

const NOMBRE_CLASE: Record<ClaseAdjunto, { un: string; consejo: string }> = {
  imagen: { un: 'una imagen', consejo: 'Redúcela y vuelve a subirla.' },
  video: { un: 'un vídeo', consejo: 'Recórtalo o bájale la resolución.' },
  documento: { un: 'un documento', consejo: 'Comprímelo o divídelo.' },
};

/**
 * Por qué NO se puede subir este fichero, o `null` si a primera vista sí.
 *
 * Devuelve el motivo redactado, no un código: quien lo llama lo enseña tal cual y no tiene
 * que traducir nada. El mensaje dice qué pasa y qué hacer, que es lo único que le sirve a
 * quien está mirando la pantalla.
 *
 * Cada clase tiene su límite —y llegan desde el servidor— porque son límites distintos: un
 * vídeo de 30 MB es normal y una «captura» de 30 MB es un error de quien la exportó.
 */
export function motivoDeRechazo(file: File, limites: LimitesMb): string | null {
  const clase = claseDe(file);
  if (!clase) {
    return (
      `«${file.name}» no es un formato admitido. Se aceptan imágenes (PNG, JPG, GIF, WebP), ` +
      'vídeos (MP4, MOV, WebM), PDF, Office (DOCX, XLSX, PPTX) y texto (MD, TXT, LOG, CSV, ' +
      'JSON, YAML).'
    );
  }

  const maxMb = limites[clase];
  if (file.size > maxMb * 1024 * 1024) {
    return (
      `«${file.name}» pesa ${(file.size / 1024 / 1024).toFixed(1)} MB y el máximo para ` +
      `${NOMBRE_CLASE[clase].un} son ${maxMb} MB. ${NOMBRE_CLASE[clase].consejo}`
    );
  }
  return null;
}
